// src/components/SystemShowcase.tsx
// Карусель скриншотов системы для лендинга
import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, LayoutDashboard, Users, BarChart3, FileText, Wallet, Briefcase } from 'lucide-react';

interface Slide {
  image: string;
  title: string;
  subtitle: string;
  accent: string;
  icon: React.ReactNode;
}

const SLIDES: Slide[] = [
  {
    image: '/images/screen-accounting.png',
    title: 'Основной дашборд',
    subtitle: 'Единая точка входа — ключевые показатели по всем модулям в одном окне',
    accent: '#6a11cb',
    icon: <LayoutDashboard size={16} />,
  },
  {
    image: '/images/screen-hrm.png',
    title: 'HRM — Кадры и найм',
    subtitle: 'Дашборд с воронкой подбора, KPI, HR-метриками и психологическими тестами',
    accent: '#2575fc',
    icon: <Users size={16} />,
  },
  {
    image: '/images/screen-crm.png',
    title: 'CRM — Продажи',
    subtitle: 'Дашборд продаж: воронка сделок, конверсия, WinRate и источники',
    accent: '#8b5cf6',
    icon: <Briefcase size={16} />,
  },
  {
    image: '/images/screen-bi.png',
    title: 'КДП — Документы',
    subtitle: 'Кадровый документооборот: приказы, шаблоны ТК РФ, онбординг, подписи',
    accent: '#f59e0b',
    icon: <FileText size={16} />,
  },
  {
    image: '/images/screen-kdp.png',
    title: 'Бухгалтерия — Финансы',
    subtitle: 'Финансовое состояние: доходы, расходы, ДДС и остатки по счетам',
    accent: '#10b981',
    icon: <Wallet size={16} />,
  },
];

const SystemShowcase: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const next = () => setCurrent((c) => (c + 1) % SLIDES.length);
  const prev = () => setCurrent((c) => (c - 1 + SLIDES.length) % SLIDES.length);
  const goTo = (i: number) => setCurrent(i);

  // Auto-advance
  useEffect(() => {
    if (isPaused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [isPaused, current]);

  // Touch handlers
  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };
  const onTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].screenX;
    handleSwipe();
  };
  const handleSwipe = () => {
    if (touchStartX.current === null || touchEndX.current === null) return;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) next(); else prev();
    }
    touchStartX.current = null;
    touchEndX.current = null;
  };

  return (
    <section className="sysshowcase-section">
      <style>{`
        .sysshowcase-section {
          padding: 80px 20px 100px;
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
        }
        .sysshowcase-header {
          text-align: center;
          margin-bottom: 40px;
        }
        .sysshowcase-title {
          font-size: clamp(24px, 4vw, 34px);
          font-weight: 800;
          color: #1a1a2e;
          margin: 0 0 12px;
        }
        .sysshowcase-subtitle {
          font-size: 16px;
          color: #64748b;
          max-width: 640px;
          margin: 0 auto;
          line-height: 1.6;
        }
        .sysshowcase-frame {
          position: relative;
          max-width: 1040px;
          margin: 0 auto 120px;
          border-radius: 28px;
          background: rgba(255,255,255,0.75);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255,255,255,0.3);
          box-shadow:
            0 30px 60px -15px rgba(37,117,252,0.35),
            0 20px 40px -10px rgba(0,0,0,0.15),
            0 0 120px -20px rgba(106,17,203,0.5);
        }
        .sysshowcase-slides {
          position: relative;
          width: 100%;
          aspect-ratio: 2.08 / 1;
          overflow: hidden;
          border-radius: 28px;
        }
        .sysshowcase-slide {
          position: absolute;
          inset: 0;
          opacity: 0;
          transform: scale(1.04);
          transition: opacity 0.6s ease, transform 0.6s ease;
          pointer-events: none;
        }
        .sysshowcase-slide.active {
          opacity: 1;
          transform: scale(1);
          pointer-events: auto;
        }
        .sysshowcase-slide img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
          display: block;
          background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
        }
        .sysshowcase-caption {
          position: absolute;
          left: 40px;
          bottom: -60px;
          right: 40px;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(20px);
          border-radius: 20px;
          padding: 18px 24px;
          display: flex;
          align-items: center;
          gap: 16px;
          box-shadow:
            0 20px 40px -10px rgba(37,117,252,0.3),
            0 10px 20px -8px rgba(0,0,0,0.15),
            0 0 60px -15px rgba(106,17,203,0.4);
          border: 1px solid rgba(255,255,255,0.8);
          max-width: 560px;
          z-index: 5;
        }
        .sysshowcase-caption-icon {
          width: 56px;
          height: 56px;
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          flex-shrink: 0;
          box-shadow: 0 12px 24px -8px currentColor;
        }
        .sysshowcase-caption-text {
          min-width: 0;
        }
        .sysshowcase-caption-title {
          font-size: 17px;
          font-weight: 800;
          color: #1a1a2e;
          margin: 0 0 2px;
        }
        .sysshowcase-caption-sub {
          font-size: 13px;
          color: #64748b;
          margin: 0;
          line-height: 1.4;
        }
        .sysshowcase-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: rgba(255,255,255,0.95);
          border: 1px solid rgba(255,255,255,0.6);
          box-shadow: 0 8px 20px rgba(0,0,0,0.15);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1a1a2e;
          transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
          z-index: 3;
        }
        .sysshowcase-arrow:hover {
          transform: translateY(-50%) scale(1.08);
          box-shadow: 0 12px 28px rgba(37,117,252,0.3);
          background: #fff;
        }
        .sysshowcase-arrow.left { left: 16px; }
        .sysshowcase-arrow.right { right: 16px; }
        .sysshowcase-dots {
          position: absolute;
          bottom: 16px;
          right: 24px;
          display: flex;
          gap: 8px;
          z-index: 3;
        }
        .sysshowcase-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255,255,255,0.5);
          border: 1px solid rgba(255,255,255,0.7);
          cursor: pointer;
          transition: all 0.25s;
          padding: 0;
        }
        .sysshowcase-dot.active {
          width: 24px;
          border-radius: 4px;
          background: #2575fc;
          border-color: #2575fc;
        }
        @media (max-width: 720px) {
          .sysshowcase-section { padding: 60px 16px 100px; }
          .sysshowcase-frame { border-radius: 20px; margin-bottom: 80px; }
          .sysshowcase-slides { border-radius: 20px; }
          .sysshowcase-arrow { width: 38px; height: 38px; }
          .sysshowcase-arrow.left { left: 10px; }
          .sysshowcase-arrow.right { right: 10px; }
          .sysshowcase-caption { left: 16px; right: 16px; bottom: -50px; padding: 16px 18px; gap: 12px; }
          .sysshowcase-caption-icon { width: 44px; height: 44px; border-radius: 12px; }
          .sysshowcase-dots { bottom: 10px; right: 12px; }
        }
      `}</style>

      <div className="sysshowcase-header">
        <h2 className="sysshowcase-title">Посмотрите, как это работает</h2>
        <p className="sysshowcase-subtitle">Единый интерфейс для найма, кадров, продаж и финансов</p>
      </div>

      <div
        className="sysshowcase-frame"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="sysshowcase-slides">
          {SLIDES.map((slide, idx) => (
            <div key={idx} className={`sysshowcase-slide ${idx === current ? 'active' : ''}`}>
              <img src={slide.image} alt={slide.title} loading="lazy" />
            </div>
          ))}

          <button type="button" className="sysshowcase-arrow left" onClick={prev} aria-label="Предыдущий">
            <ChevronLeft size={20} />
          </button>
          <button type="button" className="sysshowcase-arrow right" onClick={next} aria-label="Следующий">
            <ChevronRight size={20} />
          </button>

          <div className="sysshowcase-dots">
            {SLIDES.map((_, idx) => (
              <button
                key={idx}
                type="button"
                className={`sysshowcase-dot ${idx === current ? 'active' : ''}`}
                onClick={() => goTo(idx)}
                aria-label={`Слайд ${idx + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Подпись — вне slides, чтобы выступала за границы */}
        <div className="sysshowcase-caption">
          <div className="sysshowcase-caption-icon" style={{ background: `linear-gradient(135deg, ${SLIDES[current].accent}, ${SLIDES[current].accent}cc)` }}>
            {SLIDES[current].icon}
          </div>
          <div className="sysshowcase-caption-text">
            <p className="sysshowcase-caption-title">{SLIDES[current].title}</p>
            <p className="sysshowcase-caption-sub">{SLIDES[current].subtitle}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemShowcase;
